import {
  MONTHLY_RATIOS,
  Valuation,
  isoDayToUtcDate,
  toIsoDay,
  type RecurrenceIntervalValue,
} from "@gladis/shared";

import { inForceOn } from "./amount-in-force.js";

/**
 * WHEN a recurring payment leaves, and how much of a period is still to go out. Feature 024.
 *
 * Every other figure in this context is a MONTHLY EQUIVALENT — what the household is committed to per month.
 * This module answers a different question about the same expenses, in CASH DUE: a €600 yearly bill is €600
 * on its one date and nothing on the other 364. The two answers are both right and must never be presented
 * under each other's name, which is why this is its own module rather than a mode of `monthlyEquivalent`.
 *
 * Pure, and it cannot fail: no clock (the caller passes `today`), no I/O, no `Result`. Every day is a
 * `YYYY-MM-DD` string and every day arithmetic goes through `isoDayToUtcDate`/`toIsoDay` — a day is never a
 * local `Date` (009 research R6).
 */

export type SchedulePeriodKind = "week" | "month" | "year";

/** A calendar period, both ends INCLUSIVE. */
export interface DayWindow {
  readonly from: string;
  readonly to: string;
}

/** One row of `expense_amount_periods`, as far as a schedule cares. Sorted ascending by the caller. */
export interface AmountHistoryRow {
  readonly effectiveFrom: string;
  readonly amountInCents: bigint;
  readonly interval: RecurrenceIntervalValue;
}

/** One row of `expense_payment_schedules` / `derived_payment_schedules`. Sorted ascending by the caller. */
export interface ScheduleHistoryRow {
  readonly effectiveFrom: string;
  /** NULL is a statement — "undated from here" — and is how a clear is recorded (FR-006a). */
  readonly firstPaymentDate: string | null;
}

/**
 * A span in which ONE amount and ONE schedule are both in force: the intersection of the two histories.
 *
 * `until` is EXCLUSIVE and `null` means "still in force". A segment never starts before the expense does.
 */
export interface ScheduleSegment {
  readonly from: string;
  readonly until: string | null;
  readonly interval: RecurrenceIntervalValue;
  readonly amountInCents: bigint;
  readonly anchor: string | null;
}

const DAY_MS = 86_400_000;

/** Calendar-month intervals step from the anchor by this many months. */
const MONTH_STEPS: Partial<Record<RecurrenceIntervalValue, number>> = {
  monthly: 1,
  quarterly: 3,
  half_yearly: 6,
  yearly: 12,
};

/** Day intervals step from the anchor by this many days. Never "a month" — four weeks is not one. */
const DAY_STEPS: Partial<Record<RecurrenceIntervalValue, number>> = {
  weekly: 7,
  every_4_weeks: 28,
};

export function addDays(day: string, days: number): string {
  return toIsoDay(new Date(isoDayToUtcDate(day).getTime() + days * DAY_MS));
}

/** Whole calendar days from `from` to `to` — negative when `to` is earlier. */
export function dayDifference(from: string, to: string): number {
  return Math.round((isoDayToUtcDate(to).getTime() - isoDayToUtcDate(from).getTime()) / DAY_MS);
}

function daysInMonth(year: number, monthIndex: number): number {
  return new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate();
}

function pad(value: number): string {
  return String(value).padStart(2, "0");
}

/**
 * The calendar period containing `today` (FR-011): Monday–Sunday, the 1st to the last day of the month, or
 * 1 January to 31 December. Always the CURRENT one — there is no browsing in v1.
 */
export function periodWindow(kind: SchedulePeriodKind, today: string): DayWindow {
  const [year, month] = today.split("-").map(Number) as [number, number];
  if (kind === "year") return { from: `${year}-01-01`, to: `${year}-12-31` };
  if (kind === "month") {
    return {
      from: `${year}-${pad(month)}-01`,
      to: `${year}-${pad(month)}-${pad(daysInMonth(year, month - 1))}`,
    };
  }
  // `getUTCDay` is 0 for Sunday; Monday starts the week, so Sunday is six days after it.
  const sinceMonday = (isoDayToUtcDate(today).getUTCDay() + 6) % 7;
  const monday = addDays(today, -sinceMonday);
  return { from: monday, to: addDays(monday, 6) };
}

/**
 * Every due date of a rhythm that falls in `[from, to]`, ascending.
 *
 * ==========================================================================================
 * COMPUTED FROM THE ANCHOR, NEVER FROM THE PREVIOUS DATE — FR-003
 * ==========================================================================================
 *
 * The n-th monthly date is `min(anchorDay, daysIn(anchorMonth + n × step))`. Chaining instead — "the
 * previous due date plus one month" — is the obvious implementation and it DRIFTS: 31 Jan → 28 Feb → 28 Mar
 * → 28 Apr…, never returning to the 31st. Computed from the anchor, March is the 31st again by construction,
 * and a 29 February yearly anchor is the 28th in a common year and the 29th in the next leap year.
 *
 * ==========================================================================================
 * THE ANCHOR IS A RHYTHM, NOT A FLOOR (research R3)
 * ==========================================================================================
 *
 * Dates step BACKWARDS from the anchor as well as forwards; the caller bounds them by the span the schedule
 * covers. A member who dates an expense running since January with a recent payment would otherwise lose
 * January and February: not on the chart, not in the estimate, and a year short with nothing saying so.
 *
 * The first index is computed, not walked to, so a 2019 anchor costs the same as one set this morning.
 */
export function dueDatesBetween(
  anchor: string,
  interval: RecurrenceIntervalValue,
  from: string,
  to: string,
): string[] {
  if (from > to) return [];
  const dates: string[] = [];

  const dayStep = DAY_STEPS[interval];
  if (dayStep !== undefined) {
    let date = addDays(anchor, Math.ceil(dayDifference(anchor, from) / dayStep) * dayStep);
    while (date <= to) {
      dates.push(date);
      date = addDays(date, dayStep);
    }
    return dates;
  }

  const monthStep = MONTH_STEPS[interval] ?? 1;
  const [anchorYear, anchorMonth, anchorDay] = anchor.split("-").map(Number) as [
    number,
    number,
    number,
  ];
  const [fromYear, fromMonth] = from.split("-").map(Number) as [number, number];
  const anchorIndex = anchorYear * 12 + (anchorMonth - 1);
  const fromIndex = fromYear * 12 + (fromMonth - 1);

  // Start one step early at most: the date in `from`'s own month may fall before `from`, and is skipped.
  for (let n = Math.floor((fromIndex - anchorIndex) / monthStep); ; n++) {
    const monthIndex = anchorIndex + n * monthStep;
    const year = Math.floor(monthIndex / 12);
    const month = monthIndex - year * 12;
    const day = Math.min(anchorDay, daysInMonth(year, month));
    const date = `${year}-${pad(month + 1)}-${pad(day)}`;
    if (date > to) break;
    if (date >= from) dates.push(date);
  }
  return dates;
}

/**
 * The two histories intersected into spans where one amount and one schedule hold (data-model §4).
 *
 * Clipped at `startDate` — an expense contributes nothing before it starts (FR-018). ADJACENT SEGMENTS THAT
 * AGREE ARE MERGED: an undated span that happens to contain a second NULL row, or an amount re-stated
 * unchanged, is one span — otherwise an estimate would be rounded once per fragment and drift a cent from the
 * spec's average.
 */
export function scheduleSegments(
  amounts: readonly AmountHistoryRow[],
  schedules: readonly ScheduleHistoryRow[],
  startDate: string,
): ScheduleSegment[] {
  const boundaries = [
    ...new Set([
      startDate,
      ...amounts.map((row) => row.effectiveFrom),
      ...schedules.map((row) => row.effectiveFrom),
    ]),
  ]
    .filter((day) => day >= startDate)
    .sort();

  const segments: ScheduleSegment[] = [];
  boundaries.forEach((from, index) => {
    const amount = inForceOn(amounts, from);
    if (amount === undefined) return;
    const anchor = inForceOn(schedules, from)?.firstPaymentDate ?? null;
    const until = boundaries[index + 1] ?? null;

    const previous = segments.at(-1);
    if (
      previous !== undefined &&
      previous.until === from &&
      previous.interval === amount.interval &&
      previous.amountInCents === amount.amountInCents &&
      previous.anchor === anchor
    ) {
      segments[segments.length - 1] = { ...previous, until };
      return;
    }
    segments.push({
      from,
      until,
      interval: amount.interval,
      amountInCents: amount.amountInCents,
      anchor,
    });
  });
  return segments;
}

/**
 * Where a FIRST date is anchored in time: the start of the expense's current interval run (FR-006b, R2).
 *
 * The first date a member sets is not a change — there was nothing to change — but a statement about the
 * schedule the expense has been on. It covers the trailing run of amount periods that share today's
 * interval, which is the start date for an expense whose interval never changed. An earlier span with a
 * different interval stays undated: a date stated for a monthly bill says nothing about which day the old
 * yearly payment fell on.
 */
export function firstStatementFrom(
  amounts: readonly AmountHistoryRow[],
  startDate: string,
): string {
  const last = amounts.at(-1);
  if (last === undefined) return startDate;
  let runStart = last.effectiveFrom;
  for (let index = amounts.length - 2; index >= 0; index--) {
    const row = amounts[index];
    if (row === undefined || row.interval !== last.interval) break;
    runStart = row.effectiveFrom;
  }
  return runStart < startDate ? startDate : runStart;
}

/**
 * A full period's worth of an amount, as an exact fraction: `cents / denominator`.
 *
 * Kept as a fraction so the estimate below rounds ONCE (research R6). Month is the monthly equivalent; a
 * year is twelve of them; a week is the yearly figure over 52.
 */
export interface PeriodFigure {
  readonly cents: bigint;
  readonly denominator: bigint;
}

const PERIOD_FACTOR: Record<SchedulePeriodKind, { numerator: bigint; denominator: bigint }> = {
  month: { numerator: 1n, denominator: 1n },
  year: { numerator: 12n, denominator: 1n },
  week: { numerator: 12n, denominator: 52n },
};

export function periodFigureOf(
  amountInCents: bigint,
  interval: RecurrenceIntervalValue,
  kind: SchedulePeriodKind,
): PeriodFigure {
  const ratio = MONTHLY_RATIOS[interval];
  const factor = PERIOD_FACTOR[kind];
  return {
    cents: amountInCents * ratio.numerator * factor.numerator,
    denominator: ratio.denominator * factor.denominator,
  };
}

/**
 * The ≈ estimate for an undated span (FR-021): the period figure, pro-rated by how much of the window the
 * span covers, rounded half away from zero exactly once by `Valuation.scaledBy`.
 *
 * A wholly undated expense covers the whole window, so this IS the spec's average — €50.00 a month for a
 * €600 yearly bill, €600.00 a year, €11.54 a week. The pro-rating only matters for what FR-006b leaves
 * behind (an old span with a different interval) and for a date cleared mid-period, which would otherwise be
 * counted twice: once as the steps before the clear and again as a whole period's estimate.
 */
export function undatedEstimate(
  figure: PeriodFigure,
  overlapDays: number,
  windowDays: number,
  asOf: Date,
): Valuation {
  return Valuation.fromCents(figure.cents, asOf).scaledBy(
    BigInt(overlapDays),
    figure.denominator * BigInt(windowDays),
  );
}

/** Days of `[from, untilExclusive)` that fall inside the window. Zero when they do not meet. */
export function overlapDays(window: DayWindow, from: string, until: string | null): number {
  const start = from > window.from ? from : window.from;
  const lastDay = until === null ? window.to : addDays(until, -1);
  const end = lastDay < window.to ? lastDay : window.to;
  return start > end ? 0 : dayDifference(start, end) + 1;
}
